// NEW: UI_REVAMP
import { Box, Flex, Text, useMediaQuery } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { getPcsoDrawResultsApi, getStlDrawResultsApi } from '../../api/query/reportApi';
import Loading from '../../utils/Loading';
import { parseNumbers } from '../../utils/numberFormat';
import GradientBox from '../box/GradientBox';
import ResultCard from './ResultCard';
import ResultFilterHeader from './ResultFilterHeader';
import ResultGameTabs from './ResultGameTabs';

// Group flat draw result records by gameType
const groupResultsByGame = (records) => {
	if (!Array.isArray(records)) return [];
	const groups = {};

	records.forEach((item) => {
		if (!item) return;
		const gameTypeRaw = item.gameType || item.gameName || item.game || '';
		const gameType = gameTypeRaw.toLowerCase().replace(/\s+/g, '');
		if (!gameType) return;

		groups[gameType] = groups[gameType] || {
			gameType,
			gameLabel: gameTypeRaw,
			results: [],
			winners: 0,
		};

		const possibleArrays = [item.numbers, item.num, item.drawResult, item.result];
		const drawRecords = possibleArrays.find(
			(arr) =>
				Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null,
		) || [item];

		drawRecords.forEach((record) => {
			const time = record.draw || record.drawTimeName || record.drawTime || record.time || '';
			const rawNumbers = record.num || record.drawResult || record.result || record.numbers || '';
			const drawWinners = record.winners !== undefined ? Number(record.winners) : 0;

			groups[gameType].results.push({
				time,
				numbers: parseNumbers(rawNumbers),
				winners: drawWinners,
			});
			groups[gameType].winners += drawWinners;
		});
	});

	return Object.values(groups);
};

const ResultScreen = () => {
	const [activeGame, setActiveGame] = useState('all');
	const [isSmallerThan1024] = useMediaQuery('(max-width: 1023px)');

	// Safely retrieve context dates
	const context = useOutletContext();
	const [date, setDate, maxDate] = context || [null, null, null];

	// Local fallbacks in case context is missing/null
	const [localDate, setLocalDate] = useState(new Date());
	const [localMaxDate] = useState(new Date());

	const activeDate = date || localDate;
	const activeSetDate = setDate || setLocalDate;
	const activeMaxDate = maxDate || localMaxDate;

	const formattedDrawDate = dayjs(activeDate).format('YYYY-MM-DD HH:mm:ss');

	const pcsoResult = useQuery(
		['getPcsoDrawResultsApi', formattedDrawDate],
		() => getPcsoDrawResultsApi({ drawDate: formattedDrawDate }),
		{ keepPreviousData: true },
	);

	const stlResult = useQuery(
		['getStlDrawResultsApi', formattedDrawDate],
		() => getStlDrawResultsApi({ drawDate: formattedDrawDate }),
		{ keepPreviousData: true },
	);

	const groupedData = useMemo(() => {
		const combinedRaw = [...(pcsoResult.data || []), ...(stlResult.data || [])];
		return groupResultsByGame(combinedRaw);
	}, [pcsoResult.data, stlResult.data]);

	const filterOptions = [
		{ label: 'All', value: 'all' },
		...groupedData.map((item) => ({
			label: item.gameLabel || item.gameType,
			value: item.gameType,
			logo: true,
		})),
	];

	const filteredData =
		activeGame === 'all' ? groupedData : groupedData.filter((item) => item.gameType === activeGame);

	let listContent;
	if (pcsoResult.isPreviousData || stlResult.isPreviousData) {
		listContent = (
			<Flex justifyContent='center' alignItems='center' py={10} flex={1}>
				<Loading />
			</Flex>
		);
	} else if (filteredData.length === 0) {
		listContent = (
			<Flex justifyContent='center' alignItems='center' py={10}>
				<Text fontWeight={600} color='gray.500'>
					No results found on this date.
				</Text>
			</Flex>
		);
	} else {
		listContent = <ResultCard data={filteredData} />;
	}

	return (
		<Flex
			flexDir='column'
			w='full'
			gap={2}
			flex={1}
			minH='0'
			overflow='visible'
			px={{ base: 2, lg: 0 }}
		>
			<ResultFilterHeader
				isMobile={isSmallerThan1024}
				activeDate={activeDate}
				activeSetDate={activeSetDate}
				activeMaxDate={activeMaxDate}
			/>

			{pcsoResult.isLoading || stlResult.isLoading ? (
				<Flex justifyContent='center' alignItems='center' minH='0' flex={1} w='full'>
					<Loading />
				</Flex>
			) : (
				<Flex flexDir='column' gap={{ base: 2, lg: 3 }} flex={1} minH='0' overflow='visible'>
					{isSmallerThan1024 ? (
						<Box bg='#8674FE' py={1} px={2} mx={-2}>
							<ResultGameTabs
								options={filterOptions}
								activeValue={activeGame}
								onChange={setActiveGame}
								innerProps={{ borderRadius: '16px', py: 3, px: 1 }}
								labelProps={{ fontSize: '12px', fontWeight: 600 }}
							/>
						</Box>
					) : (
						<GradientBox borderRadius='24px'>
							<Box bg='#8674FE' borderRadius='inherit' p={2}>
								<ResultGameTabs
									options={filterOptions}
									activeValue={activeGame}
									onChange={setActiveGame}
									innerProps={{ borderRadius: 'inherit', py: 3, px: 2 }}
									labelProps={{ fontSize: '16px', fontWeight: 600 }}
								/>
							</Box>
						</GradientBox>
					)}

					<Flex
						flexDir='column'
						flex={1}
						minH='0'
						gap={2}
						mx={-3}
						pt={{ lg: 3 }}
						px={3}
						pb={5}
						overflowX='hidden'
						overflowY='auto'
						css={{
							scrollbarWidth: 'none',
							msOverflowStyle: 'none',
							'&::-webkit-scrollbar': {
								display: 'none',
							},
						}}
					>
						<GradientBox borderRadius='8px' p='1px'>
							<Box
								borderRadius='inherit'
								bgGradient='linear-gradient(180deg, #FFF 0%, #E5EDF9 100%), #D6E7FF'
								p={2}
								fontWeight={600}
								color='black'
								fontSize={{ base: '', lg: '20px' }}
							>
								{dayjs(activeDate).format('YYYY-MM-DD')}
							</Box>
						</GradientBox>

						{listContent}
					</Flex>
				</Flex>
			)}
		</Flex>
	);
};

export default ResultScreen;
